/**
 * 用户数据清理工具
 * 用于在用户退出登录或切换用户时清理所有用户相关的数据
 */

/**
 * 清理所有用户相关的 localStorage 数据
 * 这个函数会清理所有可能包含用户数据的 localStorage 键
 */
export const clearUserLocalStorage = (): void => {
  console.log('[UserDataCleanup] Starting comprehensive localStorage cleanup...');

  try {
    // 记录清理前的状态
    const beforeCount = localStorage.length;
    const beforeKeys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) beforeKeys.push(key);
    }

    console.log('[UserDataCleanup] Before cleanup:', beforeCount, 'items');
    console.log('[UserDataCleanup] Keys before cleanup:', beforeKeys);

    // 暴力清空所有 localStorage
    localStorage.clear();

    // 也清空 sessionStorage
    sessionStorage.clear();

    console.log('[UserDataCleanup] After cleanup:', localStorage.length, 'items');
    console.log('[UserDataCleanup] Comprehensive cleanup completed successfully');

  } catch (error) {
    console.error('[UserDataCleanup] Error during comprehensive cleanup:', error);

    // 如果暴力清空失败，再次尝试暴力清空（可能是权限或其他临时问题）
    try {
      console.log('[UserDataCleanup] Attempting secondary cleanup...');

      // 再次尝试暴力清空
      localStorage.clear();
      sessionStorage.clear();

      console.log('[UserDataCleanup] Secondary cleanup completed');

    } catch (fallbackError) {
      console.error('[UserDataCleanup] Secondary cleanup also failed:', fallbackError);
      console.warn('[UserDataCleanup] Unable to clear storage - this may be due to browser restrictions or storage quota issues');
    }

    // 无论如何，都要确保 Supabase 认证相关的 key 被清理（这是安全必需的）
    try {
      console.log('[UserDataCleanup] Ensuring Supabase auth tokens are cleared...');

      const criticalSupabaseKeys = [
        'supabase.auth.token',
        'supabase.auth.expires_at',
        'supabase.auth.refresh_token',
        'sb-wcbcfgplcoifnzqvpyxu-auth-token',
        'sb-nvutntvitmcydxtmfxii-auth-token'
      ];

      criticalSupabaseKeys.forEach(key => {
        try {
          localStorage.removeItem(key);
          sessionStorage.removeItem(key);
        } catch (e) {
          console.warn(`[UserDataCleanup] Failed to remove critical key "${key}":`, e);
        }
      });

      console.log('[UserDataCleanup] Critical Supabase auth cleanup completed');

    } catch (criticalError) {
      console.error('[UserDataCleanup] Critical auth cleanup failed:', criticalError);
    }
  }
};

/**
 * 清理所有用户相关的内存状态
 * 这个函数会重置所有可能包含用户数据的 Jotai atoms
 * 注意：这个函数需要在有 Jotai store 上下文的地方调用
 */
export const clearUserMemoryState = (): void => {
  console.log('[UserDataCleanup] Clearing user memory state...');

  try {
    // 注意：由于我们无法直接访问 Jotai store，
    // 实际的 atom 重置需要在组件中或有 store 访问权限的地方进行
    // 这里我们只是记录日志，实际的重置逻辑在 AuthProvider 中处理

    console.log('[UserDataCleanup] Memory state cleanup completed');

  } catch (error) {
    console.error('[UserDataCleanup] Error during memory state cleanup:', error);
  }
};

/**
 * 完整的用户数据清理
 * 清理 localStorage、sessionStorage 和内存状态
 */
export const clearAllUserData = (): void => {
  console.log('[UserDataCleanup] Starting complete user data cleanup...');

  clearUserLocalStorage();
  clearUserMemoryState();

  console.log('[UserDataCleanup] Complete user data cleanup finished');
};

/**
 * 检查是否有残留的用户数据
 * 用于调试和验证清理是否完整
 */
export const checkForRemainingUserData = (): { localStorage: string[], sessionStorage: string[] } => {
  const localStorageKeys = [];
  const sessionStorageKeys = [];

  // 检查 localStorage
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key) localStorageKeys.push(key);
  }

  // 检查 sessionStorage
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key) sessionStorageKeys.push(key);
  }

  if (localStorageKeys.length > 0 || sessionStorageKeys.length > 0) {
    console.warn('[UserDataCleanup] Remaining data found:', {
      localStorage: localStorageKeys,
      sessionStorage: sessionStorageKeys
    });
  } else {
    console.log('[UserDataCleanup] No remaining user data found');
  }

  return {
    localStorage: localStorageKeys,
    sessionStorage: sessionStorageKeys
  };
};

/**
 * 开发者工具：在控制台中暴露清理函数
 * 可以在浏览器控制台中调用 window.tempoDebug.clearUserData() 来手动清理数据
 */
if (typeof window !== 'undefined') {
  (window as any).tempoDebug = {
    clearUserData: clearAllUserData,
    checkRemainingData: checkForRemainingUserData,
    clearLocalStorage: clearUserLocalStorage,
    clearMemoryState: clearUserMemoryState
  };

  console.log('[UserDataCleanup] Debug tools available at window.tempoDebug');
}
